import { ApolloCache, ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { DeductionItem, DeductionItemProps } from "@/core/entities/deduction/deduction/DeductionItem";
import {
  IDeductionItemRepository,
  IDeductionItemRepositoryResponse,
  IColumnAlias,
  IAddDeductionItemRepositoryParams,
  IAddDeductionItemRepositoryResponse,
} from "@/core/repositories/deduction";
import { GET_DEDUCTION_ITEM } from "@/core/graphql/deduction";
import ADD_DEDUCTION_ITEM from "@/core/graphql/deduction/addDeductionItem";
interface IDeductionItemQueryResponse {
  deductionItems: {
    count: number;
    items: DeductionItemProps[];
    columns: IColumnAlias[];
  };
}

const DEFAULT_FETCH_POLICY = { refetch: false } as const;

export class DeductionItemRepository implements IDeductionItemRepository {
  constructor(private readonly client: ApolloClient) {}

  async getDeductionItems(
    params: { deduction_id: number },
    fetchPolicy: { refetch: boolean } = DEFAULT_FETCH_POLICY
  ): Promise<IDeductionItemRepositoryResponse> {
    if (fetchPolicy.refetch) {
      this.invalidateDeductionItemsCache(this.client.cache);
    }

    const { data } = await this.client.query<IDeductionItemQueryResponse>({
      query: GET_DEDUCTION_ITEM,
      variables: { params },
    });

    if (!data?.deductionItems) {
      throw new Error("Failed to get deduction items");
    }

    return {
      deductionItems: data.deductionItems.items.map(deductionItem => new DeductionItem(deductionItem)),
      columns: data.deductionItems.columns,
      count: data.deductionItems.count,
    };
  }

  async addDeductionItem(params: IAddDeductionItemRepositoryParams): Promise<IAddDeductionItemRepositoryResponse> {
    const { data } = await this.client.mutate<IAddDeductionItemRepositoryResponse>({
      mutation: ADD_DEDUCTION_ITEM,
      variables: { params },
    });

    if (!data) {
      throw new Error("Failed to add deduction item");
    }

    return {
      createDeductionItem: {
        data: data?.createDeductionItem.data ?? { newlyInsertedDeductionItems: [], skippedDeductionItems: [] },
        error: data?.createDeductionItem.error ?? { errorColumn: null, errorDetails: null, processId: null },
      },
    };
  }

  private invalidateDeductionItemsCache(cache: ApolloCache) {
    const deductionItemsKey = "deductionItems";
    const rawCache = cache.extract() as NormalizedCacheObject;
    if (!rawCache) return;

    for (const key of Object.keys(rawCache.ROOT_QUERY ?? {})) {
      const deductionItemsCacheKeyMatch = new RegExp(`^${deductionItemsKey}((.*))$`).exec(key);

      if (deductionItemsCacheKeyMatch) {
        cache.evict({
          id: "ROOT_QUERY",
          fieldName: deductionItemsKey,
        });
      }
    }
  }
}
